import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { FolderType, SubfolderPermissionsDTO, useGetSubfolderPermissions } from '@/api/generated';
import { useLocation } from '@/hooks/entities/useLocation';
import { getErrorStatus } from '@/utils/error';

import { PermissionContextVariables } from './usePermissions';

type UseSubfolderPermissionsResult = {
  value?: SubfolderPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<SubfolderPermissionsDTO, unknown>> | undefined;
};

export const useSubfolderPermissions = ({
  variables
}: {
  variables?: PermissionContextVariables;
} = {}): UseSubfolderPermissionsResult => {
  const location = useLocation();

  const organizationSlug = variables?.organizationSlug ?? location.organizationSlug;
  const locationSlug = variables?.locationSlug ?? location.locationSlug;
  const locationType = variables?.locationType ?? location.locationType;

  const {
    data: subfolderPermissions,
    error,
    isLoading,
    refetch
  } = useGetSubfolderPermissions(organizationSlug!, locationSlug!, {
    query: { enabled: !!organizationSlug && !!locationSlug && locationType === FolderType.SUB_FOLDER }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue =
    !!organizationSlug && !!locationSlug && locationType === FolderType.SUB_FOLDER && !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? subfolderPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
